import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useLanguage, Language } from '@/contexts/LanguageContext';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'fr' as Language, name: 'Français', flag: '🇫🇷' },
  { code: 'en' as Language, name: 'English', flag: '🇬🇧' },
  { code: 'es' as Language, name: 'Español', flag: '🇪🇸' },
];

export const LanguageSelector: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  const currentLanguage = languages.find(lang => lang.code === language);

  const handleLanguageChange = (newLanguage: Language) => {
    /*console.log('🔄 Language change request:', {
      current: language,
      new: newLanguage,
      currentPath: location.pathname
    });*/

    // Si déjà sur la langue demandée, ne rien faire
    if (newLanguage === language) {
      // console.log('⚠️ Already on requested language, skipping');
      return;
    }

    // Nettoyer le path de tous les préfixes de langue successifs
    const currentPath = location.pathname;
    let pathWithoutLang = currentPath
      .replace(/^\/?((?:fr|en|es)\/?)+(.*?)$/, '/$2') // Supprimer tous les préfixes successifs
      .replace(/\/+/g, '/') // Supprimer les double slashes
      .replace(/\/$/, '') || '/'; // Supprimer le slash final sauf pour la racine
    
    // Corriger les routes /games/ vers /game/
    pathWithoutLang = pathWithoutLang.replace(/\/games\//g, '/game/');
    
    /*console.log('🧹 Cleaned path:', {
      original: currentPath,
      cleaned: pathWithoutLang
    });*/
    
    // Construire la nouvelle URL selon la langue cible
    let newPath = '';
    
    if (newLanguage === 'fr') {
      // Français : pas de préfixe de langue
      newPath = pathWithoutLang === '/' ? '/' : pathWithoutLang;
    } else {
      // Anglais/Espagnol : ajouter le préfixe de langue
      newPath = `/${newLanguage}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
    }
    
    // Gérer les pages serveurs spécifiquement
    const serverMatch = pathWithoutLang.match(/^\/(serveur|server|servidor)\/(.+)$/);
    if (serverMatch) {
      const [, , serverSlug] = serverMatch;
      // console.log('🏠 Server page detected:', { serverSlug });
      
      if (newLanguage === 'fr') {
        newPath = `/serveur/${serverSlug}`;
      } else if (newLanguage === 'en') {
        newPath = `/en/server/${serverSlug}`;
      } else if (newLanguage === 'es') {
        newPath = `/es/servidor/${serverSlug}`;
      }
    }
    
    /*console.log('🎯 Final navigation:', {
      from: currentPath,
      to: newPath
    });*/
    
    // Mettre à jour le contexte de langue AVANT de naviguer
    setLanguage(newLanguage);
    
    // Rediriger vers la nouvelle URL
    navigate(newPath);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm"
          className="flex items-center space-x-2 text-slate-300 hover:text-white hover:bg-slate-800"
          aria-label={language === 'fr' ? 'Changer de langue' : language === 'es' ? 'Cambiar idioma' : 'Change language'}
        >
          <Globe className="w-4 h-4" aria-hidden="true" />
          <span className="text-lg" role="img" aria-label={currentLanguage?.name}>{currentLanguage?.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40 bg-slate-800 border-slate-700">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`flex items-center space-x-3 cursor-pointer ${
              language === lang.code 
                ? 'bg-gaming-primary text-white' 
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
          >
            <span className="text-lg">{lang.flag}</span>
            <span className="font-mono text-sm">{lang.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};